<template>
  <v-app>
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      :navigation="navigation"
      @navigate="handleNavigation"
    />
    
    <v-main class="grey lighten-4">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 font-weight-bold text-grey-800">Profile</h1>
            <p class="text-subtitle-1 text-grey-600 mt-2">Manage your personal information and account settings</p>
          </div>
        </div>

        <v-row>
          <!-- Profile Information Card -->
          <v-col cols="12" md="8">
            <v-card elevation="3" class="pa-6 mb-6">
              <div class="d-flex justify-space-between align-center mb-4">
                <h2 class="text-h5 font-weight-bold text-grey-800">Personal Information</h2>
                <v-btn color="primary" text @click="editProfile">
                  <span class="material-icons-round mr-1">edit</span>
                  Edit Profile
                </v-btn>
              </div>
              
              <v-row>
                <v-col cols="12" md="6">
                  <div class="mb-3">
                    <span class="text-subtitle-2 text-grey-600">Full Name:</span>
                    <div class="text-body-1 font-weight-medium">{{ profile.fullName }}</div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-3">
                    <span class="text-subtitle-2 text-grey-600">Email:</span>
                    <div class="text-body-1 font-weight-medium">{{ profile.email }}</div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-3">
                    <span class="text-subtitle-2 text-grey-600">Phone:</span>
                    <div class="text-body-1 font-weight-medium">{{ profile.phone || 'Not provided' }}</div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-3">
                    <span class="text-subtitle-2 text-grey-600">Business Name:</span>
                    <div class="text-body-1 font-weight-medium">{{ profile.businessName || 'Not provided' }}</div>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="mb-3">
                    <span class="text-subtitle-2 text-grey-600">Address:</span>
                    <div class="text-body-1 font-weight-medium">{{ profile.address || 'Not provided' }}</div>
                  </div>
                </v-col>
              </v-row>
            </v-card>

            <!-- Change Password Card -->
            <v-card elevation="3" class="pa-6">
              <div class="d-flex justify-space-between align-center mb-4">
                <h2 class="text-h5 font-weight-bold text-grey-800">Security</h2>
                <v-btn color="orange" text @click="showChangePasswordDialog = true">
                  <span class="material-icons-round mr-1">lock</span>
                  Change Password
                </v-btn>
              </div>
              
              <div class="mb-3">
                <span class="text-subtitle-2 text-grey-600">Password:</span>
                <div class="text-body-1 font-weight-medium">••••••••••••</div>
                <p class="text-caption text-grey-500 mt-1">Last changed: {{ lastPasswordChange || 'Never' }}</p>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="mb-3">
                <span class="text-subtitle-2 text-grey-600">Account Access:</span>
                <div class="d-flex align-center mt-2">
                  <v-btn
                    color="info"
                    outlined
                    small
                    @click="resendWelcomeEmail"
                    :loading="resendEmailLoading"
                  >
                    <span class="material-icons-round mr-1">email</span>
                    Resend Welcome Email
                  </v-btn>
                  <span class="text-caption text-grey-500 ml-3">
                    Get a new temporary password via email
                  </span>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Account Summary Card -->
          <v-col cols="12" md="4">
            <v-card elevation="3" class="pa-6">
              <h2 class="text-h5 font-weight-bold text-grey-800 mb-4">Account Summary</h2>
              
              <div class="mb-4">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-subtitle-2 text-grey-600">Account Status:</span>
                  <v-chip small :color="accountStatus.color" dark>{{ accountStatus.text }}</v-chip>
                </div>
              </div>

              <div class="mb-4">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-subtitle-2 text-grey-600">Member Since:</span>
                  <span class="text-body-2">{{ memberSince }}</span>
                </div>
              </div>

              <div class="mb-4">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-subtitle-2 text-grey-600">Total Commissions:</span>
                  <span class="text-body-2 font-weight-bold">${{ totalCommissions }}</span>
                </div>
              </div>

              <div class="mb-4">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="text-subtitle-2 text-grey-600">Active Agents:</span>
                  <span class="text-body-2">{{ activeAgents }}</span>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Edit Profile Dialog -->
    <v-dialog v-model="showProfileDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5 primary--text">
          <span class="material-icons-round mr-2">person</span>
          Edit Profile
        </v-card-title>
        <v-card-text>
          <v-form ref="profileForm" v-model="profileFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editProfileForm.firstName"
                  label="First Name"
                  :rules="[v => !!v || 'First name is required']"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editProfileForm.lastName"
                  label="Last Name"
                  :rules="[v => !!v || 'Last name is required']"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editProfileForm.email"
                  label="Email"
                  :rules="emailRules"
                  outlined
                  disabled
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editProfileForm.phone"
                  label="Phone"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editProfileForm.businessName"
                  label="Business Name"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editProfileForm.address"
                  label="Address"
                  rows="3"
                  outlined
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeProfileDialog">Cancel</v-btn>
          <v-btn 
            color="green" 
            dark
            @click="saveProfile" 
            :loading="saveProfileLoading"
            :disabled="!profileFormValid"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Change Password Dialog -->
    <v-dialog v-model="showChangePasswordDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5 orange--text">
          <span class="material-icons-round mr-2">lock</span>
          Change Password
        </v-card-title>
        <v-card-text>
          <v-form ref="passwordForm" v-model="passwordFormValid">
            <v-text-field
              v-model="passwordForm.currentPassword"
              label="Current Password"
              :type="showCurrentPassword ? 'text' : 'password'"
              @click:append="showCurrentPassword = !showCurrentPassword"
              :rules="[v => !!v || 'Current password is required']"
              outlined
            ></v-text-field>
            
            <v-text-field
              v-model="passwordForm.newPassword"
              label="New Password"
              :type="showNewPassword ? 'text' : 'password'"
              @click:append="showNewPassword = !showNewPassword"
              :rules="passwordRules"
              outlined
            ></v-text-field>
            
            <v-text-field
              v-model="passwordForm.confirmPassword"
              label="Confirm New Password"
              :type="showConfirmPassword ? 'text' : 'password'"
              @click:append="showConfirmPassword = !showConfirmPassword"
              :rules="confirmPasswordRules"
              outlined
            ></v-text-field>

            <v-checkbox
              v-model="passwordForm.logoutOtherDevices"
              label="Log out from all other devices"
              color="orange"
              hide-details
            ></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closePasswordDialog">Cancel</v-btn>
          <v-btn 
            color="orange"
            dark 
            @click="changePassword" 
            :loading="changePasswordLoading"
            :disabled="!passwordFormValid"
          >
            Change Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '../Layout/HeaderDealerDashboard.vue'
import FooterDealer from '../Layout/FooterDealer.vue'

export default {
  name: 'DealerProfile',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      userName: '',
      businessName: '',
      navigation: [
        { text: 'Dashboard', icon: 'dashboard', route: '/dealers/dashboard' },
        { text: 'Commission', icon: 'trending_up', route: '/dealers/commission' },
        { text: 'Inventory', icon: 'inventory', route: '/dealers/inventory' },
        { text: 'Distribution', icon: 'share', route: '/dealers/distribution' },
        { text: 'Referring Agents', icon: 'people', route: '/dealers/agents' },
        { text: 'Reviews', icon: 'star', route: '/dealers/reviews' },
        { text: 'Payouts', icon: 'payment', route: '/dealers/payouts' },
        { text: 'Profile', icon: 'person', route: '/dealers/profile', active: true },
        { text: 'Settings', icon: 'settings', route: '/dealers/settings' }
      ],
      profile: {
        fullName: '',
        email: '',
        phone: '',
        businessName: '',
        address: ''
      },
      accountStatus: {
        text: 'Active',
        color: 'green'
      },
      memberSince: '',
      totalCommissions: '0.00',
      activeAgents: 0,
      lastPasswordChange: '',
      showProfileDialog: false,
      showChangePasswordDialog: false,
      profileFormValid: false,
      passwordFormValid: false,
      saveProfileLoading: false,
      changePasswordLoading: false,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      editProfileForm: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        businessName: '',
        address: ''
      },
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        logoutOtherDevices: false
      },
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        v => (v && v.length >= 6) || 'Password must be at least 6 characters'
      ]
    }
  },
  computed: {
    confirmPasswordRules() {
      return [
        v => !!v || 'Please confirm your password',
        v => v === this.passwordForm.newPassword || 'Passwords do not match'
      ]
    }
  },
  async mounted() {
    this.loadUserData()
    await this.loadProfile()
  },
  methods: {
    handleNavigation(item) {
      // Navigation is handled by the HeaderDealerDashboard component
    },
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Your Business'
    },
    async loadProfile() {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          this.$router.push('/dealers/login')
          return
        }

        const response = await axios.get('/api/dealer/profile', {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.data.flag === 1) {
          const data = response.data.data
          this.profile = {
            fullName: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
            email: data.email || '',
            phone: data.phone || '',
            businessName: data.business_name || '',
            address: data.address || ''
          }

          // Set account summary data
          this.memberSince = data.created_at ? new Date(data.created_at).toLocaleDateString() : 'Unknown'
          this.totalCommissions = data.total_commissions || '0.00'
          this.activeAgents = data.active_agents || 0
          this.lastPasswordChange = data.last_password_change ? new Date(data.last_password_change).toLocaleDateString() : null

          // Set account status
          if (data.status === 'active') {
            this.accountStatus = { text: 'Active', color: 'green' }
          } else if (data.status === 'pending') {
            this.accountStatus = { text: 'Pending', color: 'orange' }
          } else {
            this.accountStatus = { text: 'Inactive', color: 'red' }
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error)
        if (error.response?.status === 401) {
          this.$router.push('/dealers/login')
        } else {
          this.$toast.error('Error loading profile data')
        }
      }
    },
    editProfile() {
      // Split full name into first and last name
      const nameParts = this.profile.fullName.split(' ')
      this.editProfileForm = {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: this.profile.email,
        phone: this.profile.phone,
        businessName: this.profile.businessName,
        address: this.profile.address
      }
      this.showProfileDialog = true
    },
    closeProfileDialog() {
      this.showProfileDialog = false
      this.editProfileForm = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        businessName: '',
        address: ''
      }
      this.$refs.profileForm?.resetValidation()
    },
    async saveProfile() {
      try {
        if (!this.$refs.profileForm.validate()) {
          return
        }

        this.saveProfileLoading = true

        const token = localStorage.getItem('token')
        const response = await axios.put('/api/dealer/profile', {
          first_name: this.editProfileForm.firstName,
          last_name: this.editProfileForm.lastName,
          phone: this.editProfileForm.phone,
          business_name: this.editProfileForm.businessName,
          address: this.editProfileForm.address
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.data.flag === 1) {
          // Update profile data
          this.profile.fullName = `${this.editProfileForm.firstName} ${this.editProfileForm.lastName}`.trim()
          this.profile.phone = this.editProfileForm.phone
          this.profile.businessName = this.editProfileForm.businessName
          this.profile.address = this.editProfileForm.address

          // Update localStorage
          localStorage.setItem('user_name', this.profile.fullName)
          localStorage.setItem('business_name', this.profile.businessName)
          this.userName = this.profile.fullName
          this.businessName = this.profile.businessName

          this.$toast.success('Profile updated successfully!')
          this.closeProfileDialog()
        } else {
          this.$toast.error(response.data.message || 'Error updating profile')
        }
      } catch (error) {
        console.error('Error saving profile:', error)
        this.$toast.error('Error updating profile')
      } finally {
        this.saveProfileLoading = false
      }
    },
    closePasswordDialog() {
      this.showChangePasswordDialog = false
      this.passwordForm = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        logoutOtherDevices: false
      }
      this.showCurrentPassword = false
      this.showNewPassword = false
      this.showConfirmPassword = false
      this.$refs.passwordForm?.resetValidation()
    },
    async changePassword() {
      try {
        if (!this.$refs.passwordForm.validate()) {
          return
        }

        this.changePasswordLoading = true

        const token = localStorage.getItem('token')
        const response = await axios.post('/api/dealer/change-password', {
          current_password: this.passwordForm.currentPassword,
          new_password: this.passwordForm.newPassword,
          logout_other_devices: this.passwordForm.logoutOtherDevices
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Password changed successfully!')
          this.closePasswordDialog()

          // If logout other devices was selected, redirect to login
          if (this.passwordForm.logoutOtherDevices) {
            this.$toast.info('You will be redirected to login page in 3 seconds...')
            setTimeout(() => {
              localStorage.clear()
              this.$router.push('/dealers/login')
            }, 3000)
          }
        } else {
          this.$toast.error(response.data.message || 'Error changing password')
        }
      } catch (error) {
        console.error('Error changing password:', error)
        if (error.response?.status === 400) {
          this.$toast.error(error.response.data.message || 'Invalid current password')
        } else {
          this.$toast.error('Error changing password')
        }
      } finally {
        this.changePasswordLoading = false
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.text-grey-800 {
  color: #424242 !important;
}

.text-grey-600 {
  color: #757575 !important;
}

.text-grey-500 {
  color: #9E9E9E !important;
}

.v-chip {
  font-size: 12px;
  height: 24px;
}
</style>
